import { v4 as uuidv4 } from 'uuid';
import type { NetMessage, PeerId } from './transport';
import type { SavedGroundItem } from '../types/save.types';

const CLIENT_TOKEN_KEY = 'usagi-client-token';

/** Fixed dev lobby code so 2-browser tests skip the random-code copy step. */
export const DEV_LOBBY_CODE = 'AAAA';

/** Auto-reconnect window before giving up and returning to the client's hub. */
export const RECONNECT_WINDOW_MS = 20000;

/** Stable per-browser id, persisted once. Solo never reads it. */
export function getClientToken(): string {
  let token = localStorage.getItem(CLIENT_TOKEN_KEY);
  if (!token) {
    token = uuidv4();
    localStorage.setItem(CLIENT_TOKEN_KEY, token);
  }
  return token;
}

const COOP_RESUME_KEY = 'usagi-coop-resume';

export interface CoopResumeRecord {
  code: string;
  roomId: string;
}

/** Persist that this client is in a live co-op dungeon under `code` (with the
 *  room's `roomId` to reject a same-code stranger on rejoin), so a reload
 *  (F5 / crash) can auto-rejoin at boot. Cleared on every clean exit. */
export function setCoopResume(code: string, roomId: string): void {
  localStorage.setItem(
    COOP_RESUME_KEY,
    JSON.stringify({ code, roomId } satisfies CoopResumeRecord),
  );
}
export function getCoopResume(): CoopResumeRecord | null {
  const raw = localStorage.getItem(COOP_RESUME_KEY);
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as Partial<CoopResumeRecord>;
    if (!parsed.code) {
      return null;
    }
    return { code: parsed.code, roomId: parsed.roomId ?? '' };
  } catch {
    return null;
  }
}
export function clearCoopResume(): void {
  localStorage.removeItem(COOP_RESUME_KEY);
}

const COOP_GROUND_KEY = 'usagi-coop-ground';

interface CoopGroundRecord {
  floor: number;
  items: SavedGroundItem[];
}

export function setCoopGround(floor: number, items: SavedGroundItem[]): void {
  localStorage.setItem(
    COOP_GROUND_KEY,
    JSON.stringify({ floor, items } satisfies CoopGroundRecord),
  );
}

export function getCoopGround(): CoopGroundRecord | null {
  const raw = localStorage.getItem(COOP_GROUND_KEY);
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw) as CoopGroundRecord;
  } catch {
    return null;
  }
}

export function clearCoopGround(): void {
  localStorage.removeItem(COOP_GROUND_KEY);
}

export interface ReconnectRequestMsg extends NetMessage {
  type: 'reconnect-request';
  token: string;
}
export interface ReconnectAcceptMsg extends NetMessage {
  type: 'reconnect-accept';
  playerId: string;
  downed: boolean;
}
export interface ReconnectRejectMsg extends NetMessage {
  type: 'reconnect-reject';
}
export function makeReconnectRequest(token: string): ReconnectRequestMsg {
  return { type: 'reconnect-request', token };
}
export function makeReconnectAccept(
  playerId: string,
  downed: boolean,
): ReconnectAcceptMsg {
  return { type: 'reconnect-accept', playerId, downed };
}
export const RECONNECT_REJECT: ReconnectRejectMsg = {
  type: 'reconnect-reject',
};
export interface RunEndedMsg extends NetMessage {
  type: 'run-ended';
}
/** Host (in hub) → a reconnecting client: the run is over but the session is
 *  still open. The client joins the shared hub instead of waiting to reclaim a
 *  no-longer-existing avatar. */
export const RUN_ENDED: RunEndedMsg = { type: 'run-ended' };

/**
 * Host-side reconnect state for the current run. `tokenToPlayer` is filled at
 * run-start by the LoadoutGate; `peerRoute` maps a reconnected peer's NEW id to
 * its original player (avatar) id. Default route is identity, so solo / no-reco
 * paths are unaffected.
 */
class ReconnectRegistry {
  private tokenToPlayer = new Map<string, PeerId>();
  private peerRoute = new Map<PeerId, PeerId>();

  registerToken(token: string, playerId: PeerId): void {
    this.tokenToPlayer.set(token, playerId);
  }
  playerForToken(token: string): PeerId | undefined {
    return this.tokenToPlayer.get(token);
  }
  /** Reverse lookup token → playerId, for saving the co-op party. */
  tokenForPlayer(playerId: PeerId): string | undefined {
    for (const [token, pid] of this.tokenToPlayer) {
      if (pid === playerId) {
        return token;
      }
    }
    return undefined;
  }
  route(newPeerId: PeerId, playerId: PeerId): void {
    this.peerRoute.set(newPeerId, playerId);
  }
  clearRoute(peerId: PeerId): void {
    this.peerRoute.delete(peerId);
  }
  /** Purge a player from the registry entirely (deliberate leave): its token
   *  mapping and ALL routes pointing at it. After this, that token can no longer
   *  reclaim an avatar. */
  forgetPlayer(playerId: PeerId): void {
    for (const [token, pid] of this.tokenToPlayer) {
      if (pid === playerId) {
        this.tokenToPlayer.delete(token);
      }
    }
    for (const [peer, pid] of this.peerRoute) {
      if (pid === playerId) {
        this.peerRoute.delete(peer);
      }
    }
  }
  resolvePlayerId(from: PeerId): PeerId {
    return this.peerRoute.get(from) ?? from;
  }
  reset(): void {
    this.tokenToPlayer.clear();
    this.peerRoute.clear();
  }
}
export const reconnectRegistry = new ReconnectRegistry();

/**
 * Client-side: the avatar id this browser controls. After a reconnect, the new
 * peerId differs from the avatar id (kept host-side), and applyReplicatedFloor's
 * `dungeonService.reset()` nulls localPlayerId — so the bound id must survive
 * outside dungeonService. Set on reconnect-accept, cleared on teardown.
 */
let boundPlayerId: string | null = null;
export function setBoundPlayerId(id: string | null): void {
  boundPlayerId = id;
}
export function getBoundPlayerId(): string | null {
  return boundPlayerId;
}
